import { HttpRequestError } from "@workspace/base-api/fetchJson";

/**
 * HA-877 (Chris, 2026-10-06: enforce at send and pay) — the server refuses send-to-kitchen, a cash or card payment
 * and complete-order while a line has an unanswered REQUIRED option group, 400 with this code and the group and line
 * named. D-HA-72 (HA-1025): the till asks for those options when the product is tapped, so in normal use this no
 * longer fires; it stays as the backstop (72-4) for a line added before that, and the till opens that line's editor.
 */
export const ORDER_LINE_OPTION_REQUIRED_CODE = "ORDER_LINE_OPTION_REQUIRED";

export interface OptionRequired {
	detail: string;
	groupName: string;
	lineId: string;
	lineName: string;
}

/** The refusal's group and line, or null for any other error. */
export function optionRequiredOf(error: unknown): OptionRequired | null {
	if (
		!(error instanceof HttpRequestError) ||
		error.problem?.code !== ORDER_LINE_OPTION_REQUIRED_CODE
	) {
		return null;
	}

	const problem = error.problem as unknown as Record<string, string>;
	return {
		detail: problem.detail ?? "A required choice is missing.",
		groupName: problem.groupName ?? "",
		lineId: problem.lineId ?? "",
		lineName: problem.lineName ?? "",
	};
}

/** A send is retried for a lost connection or a server fault, up to three times: never for a refusal (a 400 is final). */
export function retrySend(failureCount: number, error: unknown): boolean {
	if (error instanceof HttpRequestError && error.response.status < 500) {
		return false;
	}
	return failureCount < 3;
}

/** The lines the optimistic send marked sent, back to unsent, when the server refused. */
export function unmarkSent<T extends { id: string; sentToKitchen?: boolean }>(
	items: T[],
	markedIds: string[],
): T[] {
	const marked = new Set(markedIds);
	return items.map((i) =>
		marked.has(i.id) ? { ...i, sentToKitchen: false } : i,
	);
}
